import { useLazyQuery, useMutation } from '@apollo/client';
import { Flags } from '@constants/flags';
import { WorkflowObjectTypes } from '@generated/graphql';
import type { WorkflowCompletion } from '@interfaces/workflow';
import { useFlag } from '@onevesthq/feature-flags/web';
import { useClientContext } from '@pages/ClientDetail/clientContext';
import { useHouseholdContext } from '@pages/HouseholdDetail/householdContext';
import { ActionContext } from '@pages/ViewDetail/actionContext';
import { UserContext } from '@providers/userContextProvider';
import {
  buildInnerWorkflowLaunchInput,
  type InnerWorkflowRootIds,
} from '@utils/buildWorkflowLaunchInput';
import {
  type ComponentProps,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useWorkflowLaunchError } from '../../../../hooks/useWorkflowLaunchInput';
import {
  CREATE_WORKFLOW_COMPLETION,
  UPDATE_WORKFLOW_COMPLETION,
} from '../subSteps/queries';
import { WorkflowContext } from '../workflowCompletionContext';
import type { WorkflowDataType } from '../workflowTypes';
import { InnerWorkflowModal } from './innerWorkflowModal';
import { INNER_WORKFLOW_FETCH_WORKFLOW_OBJECT_TYPE } from './useInnerWorkflow.query';
import type { WorkflowCompletionWithId } from './workflowCompletionWithId.types';

export const useInnerWorkflow = ({
  workflowCompletion,
  refetch,
}: {
  workflowCompletion: WorkflowCompletionWithId;
  refetch: any;
}) => {
  const { workflowData } = useContext(WorkflowContext);
  const { refetch: refetchWorkflow } = useContext(ActionContext);
  const [innerOpen, setInnerOpen] = useState(false);
  const [newActiveWorkflow, setNewActiveWorkflow] =
    useState<WorkflowCompletion>();
  const [workflowCompletedCallback, setWorkflowCompletedCallback] = useState<
    ((completedContext: any) => void) | null
  >(null);
  const [updateWorkflowCompletion] = useMutation(UPDATE_WORKFLOW_COMPLETION());
  const [createWorkflowCompletionMutation] = useMutation(
    CREATE_WORKFLOW_COMPLETION(),
  );

  const [fetchWorkflowObjectType] = useLazyQuery(
    INNER_WORKFLOW_FETCH_WORKFLOW_OBJECT_TYPE,
    { fetchPolicy: 'cache-first' },
  );
  const { enabled: withContext } = useFlag(Flags.context_aware_workflows, {
    default: false,
  });
  const reportLaunchError = useWorkflowLaunchError();
  const clientContext = useClientContext();
  const { activeOrganization } = useContext(UserContext);
  const householdContext = useHouseholdContext();
  const organizationId =
    clientContext?.orgSettings?.id ||
    householdContext?.orgSettings?.id ||
    activeOrganization?.id;

  const updateParentWorkflowCompletion = async (
    parentWorkflowComplationId: string,
    updatedContext: any,
  ) => {
    await updateWorkflowCompletion({
      variables: {
        input: {
          workflowCompletionId: parentWorkflowComplationId,
          context: updatedContext ?? undefined,
        },
      },
    });
  };
  const fetchChildWorkflowObjectType = async (workflowId: string) => {
    if (!withContext) return WorkflowObjectTypes.User;
    const { data } = await fetchWorkflowObjectType({
      // createFailed is toasted below; skip the global error toast.
      variables: { workflowId, skipErrorHandler: true } as {
        workflowId: string;
      },
    });
    return data?.fetchWorkflow?.workflow?.objectType;
  };

  const createWorkflowCompletion = async (
    options: InnerWorkflowRootIds & {
      workflowId: string;
      workflowContext?: WorkflowDataType;
      onWorkflowCompleted?: (completedContext: any) => void;
    },
  ) => {
    const objectType = await fetchChildWorkflowObjectType(options.workflowId);
    if (!objectType) {
      reportLaunchError('createFailed');
      return;
    }
    const launch = buildInnerWorkflowLaunchInput({
      objectType,
      rootIds: options,
      withContext,
    });
    if ('error' in launch) {
      reportLaunchError(launch.error);
      return;
    }
    const result = await createWorkflowCompletionMutation({
      variables: {
        input: {
          ...launch.input,
          workflowId: options.workflowId,
          organizationId,
          parentWorkflowCompletionId: workflowCompletion.id,
        },
      },
    });
    const { data } = result;
    setNewActiveWorkflow({
      ...data.createWorkflowCompletion.workflowCompletion,
      context: options?.workflowContext || workflowData,
    });
    // Store the completion callback for this workflow
    if (options.onWorkflowCompleted)
      setWorkflowCompletedCallback(() => options.onWorkflowCompleted);
    if (workflowCompletion?.id) {
      const updatingContext = {
        ...workflowData,
        childWorkflowCompletion:
          data.createWorkflowCompletion.workflowCompletion,
      };
      await updateParentWorkflowCompletion(
        workflowCompletion.id,
        updatingContext,
      );
    }
    setInnerOpen(true);
    return result;
  };

  const lastContextRef = useRef();
  const onUpdateParent: ComponentProps<
    typeof InnerWorkflowModal
  >['updateParent'] = async (activeCompletion?: any) => {
    if (!activeCompletion) {
      delete workflowData.childWorkflowCompletion;
      await updateParentWorkflowCompletion(workflowCompletion.id, {
        ...workflowData,
      });

      // Call the completion callback if provided and workflow is completed
      await workflowCompletedCallback?.(lastContextRef.current);
    } else {
      const updatingContext = {
        ...workflowData,
        childWorkflowCompletion: activeCompletion,
      };
      await updateParentWorkflowCompletion(
        workflowCompletion.id,
        updatingContext,
      );
      lastContextRef.current = activeCompletion.context;
    }
  };

  const onRefetch: ComponentProps<typeof InnerWorkflowModal>['refetch'] =
    async () => {
      await Promise.all([refetchWorkflow(), refetch()]);
    };

  useEffect(() => {
    if (workflowCompletion?.context?.childWorkflowCompletion) {
      setNewActiveWorkflow({
        ...workflowCompletion?.context?.childWorkflowCompletion,
        context: workflowData,
      });
    }
  }, [workflowCompletion, workflowData]);

  return {
    createWorkflowCompletion,
    innerWorkflowModal: newActiveWorkflow && (
      <InnerWorkflowModal
        newActiveWorkflow={newActiveWorkflow}
        setOpen={setInnerOpen}
        open={innerOpen}
        refetch={onRefetch}
        updateParent={onUpdateParent}
      />
    ),
  };
};
